import { InboxOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Upload, type UploadProps } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useMemo } from "react";
import { FileListItem } from "../../../common/components/FileListItem";
import type { ModelFile } from "../../generated-axios-client";
import { startUpload } from "../store";
import styles from "./FileItem.module.css";

function getUploadConfig(
  formOnChange: (item: any) => void,
  dispatch: Dispatch,
  intl: IntlShape,
  notifficationApi?: HookAPI
): UploadProps {
  return {
    multiple: true,
    action: undefined,
    customRequest: (options) => {
      if (options.file instanceof File) {
        dispatch(startUpload(options.file, options.onSuccess, options.onError));
      } else {
        notifficationApi?.warning({
          title: intl.formatMessage({ id: "upload_failed" }),
        });
      }
    },
    onChange: (info: UploadChangeParam<UploadFile<ModelFile>>) => {
      const new_files = info.fileList
        .filter((file) => file.status === "done" && file.response)
        .reduce(
          (obj, file) => {
            if (file.response?.file_path) {
              obj[file.response?.file_path] = {
                name: file.response.name,
                size: file.response.size,
                content_type: file.response.content_type,
                path: file.response.file_path,
              };
            }
            return obj;
          },
          {} as Record<string, any>
        );
      formOnChange(new_files);
    },
  };
}

type FromDropzoneProps = {
  dispatch: Dispatch;
  onChange?: (item: any) => void;
  notifficationApi?: HookAPI;
  intl: IntlShape;
};

export function FormDropzone(props: FromDropzoneProps) {
  const { onChange, intl, dispatch, notifficationApi } = props;

  const uploadConfig = useMemo(() => {
    if (!onChange) {
      return;
    }
    return getUploadConfig(onChange, dispatch, intl, notifficationApi);
  }, [onChange, dispatch, notifficationApi, intl]);

  return (
    <Upload.Dragger
      {...uploadConfig}
      className={styles.uploadWrapper}
      itemRender={(_, file, __, actions) => (
        <FileListItem file={file} onRemove={actions.remove} intl={intl} />
      )}
    >
      <p className="ant-upload-drag-icon">
        <InboxOutlined />
      </p>
      <p className="ant-upload-text">
        {intl.formatMessage({ id: "upload_choose_files" })}
      </p>
      <p className="ant-upload-hint">{intl.formatMessage({ id: "upload_hint" })}</p>
    </Upload.Dragger>
  );
}
